import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Copy, Check, Boxes, BrainCircuit, Cpu, Map, Package, Database, Shield, Users, Layers, Loader2, Waves } from 'lucide-react';
import { createAppInstance, getAppCursorRules, getAppSettings, testMongoConnection } from '../api';

interface AppCreateFormProps {
  onCreated: (app: any) => void;
}

// GitHub caps repo descriptions at 350 chars; the app description seeds the repo
// description on provisioning, so keep them in lockstep to avoid 422 on create.
const APP_DESCRIPTION_MAX = 350;

type StorageMode = 'shared' | 'dedicated';

const CORE_FEATURES = [
  { id: 'entity_manager', label: 'Entity Manager', description: 'Legal entities, companies, contacts', icon: Users },
  { id: 'firebase_auth', label: 'Firebase Auth', description: 'User authentication & login', icon: Shield },
  { id: 'pim', label: 'PIM', description: 'Product information management', icon: Database },
  { id: 'dam', label: 'DAM', description: 'Digital asset management', icon: Layers },
  { id: 'contextualizer', label: 'Contextualizer', description: 'Templates & standards', icon: Boxes },
  { id: 'relationships', label: 'Relationships', description: 'Entity relationship graph', icon: Boxes },
];

const OPTIONAL_FEATURES = [
  { id: 'mapper_app', label: 'Mapper App', description: 'Geospatial infrastructure mapping', icon: Map },
  { id: 'asset_manager', label: 'Asset Manager', description: 'Physical asset tracking', icon: Package },
  { id: 'iot_shack', label: 'IoT Shack', description: 'Devices & telemetry', icon: Cpu },
];

const DATA_SOURCE_OPTIONS = [
  { id: 'memory', label: 'Living Memory', description: 'Public memory APIs plus MCP recall/store patterns', icon: BrainCircuit },
  { id: 'entity', label: 'Identity & Entities', description: 'Runtime entity and identity reads for app users', icon: Shield },
  { id: 'pim', label: 'PIM', description: 'Product and catalog access via governed public APIs', icon: Database },
  { id: 'dam', label: 'DAM', description: 'Assets, documents, and media through the governed DAM surface', icon: Layers },
  { id: 'iot_devices', label: 'IoT Devices', description: 'Device inventory and metadata from the public IoT gateway', icon: Cpu },
  { id: 'iot_telemetry', label: 'IoT Telemetry', description: 'Telemetry reads and analytics from the public IoT gateway', icon: Waves },
  { id: 'api_pipeline', label: 'Can read data from its API feeds', description: 'Governed runtime access to exposed external API data', icon: Boxes },
];

const STORAGE_MODE_OPTIONS: Array<{ id: StorageMode; label: string }> = [
  { id: 'shared', label: 'Internal (platform managed)' },
  { id: 'dedicated', label: '3rd-party MongoDB' },
];

export function AppCreateForm({ onCreated }: AppCreateFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [dataSources, setDataSources] = useState<Record<string, boolean>>({
    memory: true,
    entity: true,
  });
  const [loading, setLoading] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [provisioningStatus, setProvisioningStatus] = useState<string | null>(null);
  const [provisioningLogs, setProvisioningLogs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [storageMode, setStorageMode] = useState<StorageMode>('shared');
  const [dedicatedMongoUri, setDedicatedMongoUri] = useState('');
  const [extMongoHost, setExtMongoHost] = useState('');
  const [extMongoUser, setExtMongoUser] = useState('');
  const [extMongoPass, setExtMongoPass] = useState('');
  const [extMongoDb, setExtMongoDb] = useState('');
  const [extMongoUseSrv, setExtMongoUseSrv] = useState(true);
  const [mongoTestState, setMongoTestState] = useState<{ status: 'idle' | 'testing' | 'ok' | 'error'; message?: string }>({ status: 'idle' });
  const storageModeRefs = React.useRef<Record<StorageMode, HTMLButtonElement | null>>({
    shared: null,
    dedicated: null,
  });
  const [setupPrompt, setSetupPrompt] = useState<string | null>(null);
  const [setupPromptLoading, setSetupPromptLoading] = useState(false);
  const [setupPromptError, setSetupPromptError] = useState<string | null>(null);

  function toggleFeature(id: string) {
    setFeatures(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleDataSource(id: string) {
    setDataSources(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  }

  function buildDedicatedMongoUri() {
    const raw = dedicatedMongoUri.trim();
    if (raw) return raw;
    const host = extMongoHost.trim();
    if (!host) return '';
    const protocol = extMongoUseSrv ? 'mongodb+srv' : 'mongodb';
    const userPart = extMongoUser.trim()
      ? `${encodeURIComponent(extMongoUser.trim())}:${encodeURIComponent(extMongoPass)}@`
      : '';
    const dbPart = extMongoDb.trim() ? `/${encodeURIComponent(extMongoDb.trim())}` : '';
    return `${protocol}://${userPart}${host}${dbPart}?retryWrites=true&w=majority`;
  }

  function handleStorageModeKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, currentMode: StorageMode) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      setStorageMode(currentMode);
      return;
    }

    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      return;
    }

    event.preventDefault();
    const currentIndex = STORAGE_MODE_OPTIONS.findIndex((option) => option.id === currentMode);
    const delta = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
    const nextIndex = (currentIndex + delta + STORAGE_MODE_OPTIONS.length) % STORAGE_MODE_OPTIONS.length;
    const nextMode = STORAGE_MODE_OPTIONS[nextIndex].id;
    setStorageMode(nextMode);
    storageModeRefs.current[nextMode]?.focus();
  }

  async function handleTestConnection() {
    const uri = buildDedicatedMongoUri();
    if (!uri) {
      setMongoTestState({ status: 'error', message: 'Provide Mongo URI or host details first.' });
      return;
    }
    setMongoTestState({ status: 'testing', message: 'Testing connection...' });
    try {
      const result = await testMongoConnection(uri);
      if (!result?.ok) {
        const hint = [result?.code, result?.detail].filter(Boolean).join(': ');
        setMongoTestState({ status: 'error', message: hint || 'Connection failed' });
        return;
      }
      setMongoTestState({ status: 'ok', message: 'Connection successful' });
    } catch (err: any) {
      setMongoTestState({ status: 'error', message: err?.message || 'Connection test failed' });
    }
  }

  // Poll for provisioning progress
  useEffect(() => {
    if (!provisioning || !result?.app_id) return;
    
    let isSubscribed = true;
    const startedAt = Date.now();
    let warnedLongRun = false;
    const interval = setInterval(async () => {
      try {
        const settingsRes = await getAppSettings(result.app_id);
        const builder = settingsRes?.settings?.builder || {};
        const provisionData = builder?.provision;
        const status = provisionData?.status;
        
        if (!isSubscribed) return;
        
        setProvisioningStatus(status || 'provisioning');
        
        // Add log lines if we have new results
        const newLogs: string[] = [];
        if (builder?.plan_meta?.hash) newLogs.push('✅ Plan generated');
        if (builder?.layout?.components?.length) newLogs.push('✅ Layout generated');
        
        const summary = provisionData?.results?.agent_manager?.execution_summary || provisionData?.summary;
        if (summary) {
           newLogs.push(`🔄 Orchestrator: ${summary.successful_steps || 0}/${summary.total_steps || 0} steps complete`);
        }
        
        if (!warnedLongRun && (Date.now() - startedAt) > 90_000 && status !== 'completed' && status !== 'failed') {
          warnedLongRun = true;
          newLogs.push('⏳ Still provisioning after 90s. Check audit events if this persists.');
        }

        if (newLogs.length > 0) {
          // Merge + de-dupe while preserving order
          setProvisioningLogs((prev) => {
            const seen = new Set(prev);
            const merged = [...prev];
            for (const line of newLogs) {
              if (!seen.has(line)) {
                merged.push(line);
                seen.add(line);
              }
            }
            return merged;
          });
        }

        if (status === 'completed' || status === 'failed') {
          setProvisioning(false);
          // Auto-trigger onCreated only on success, to refresh the parent list
          if (status === 'completed') {
            onCreated(result);
          }
        }
      } catch (err) {
        console.error('Failed to poll app settings', err);
      }
    }, 2500);

    return () => {
      isSubscribed = false;
      clearInterval(interval);
    };
  }, [provisioning, result, onCreated]);

  useEffect(() => {
    if (storageMode !== 'dedicated') {
      setMongoTestState({ status: 'idle' });
      return;
    }
    if (mongoTestState.status === 'ok') {
      setMongoTestState({ status: 'idle' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageMode, dedicatedMongoUri, extMongoHost, extMongoUser, extMongoPass, extMongoDb, extMongoUseSrv]);

  useEffect(() => {
    if (!result?.app_id || provisioning) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 12; // 12 × 5s = 60s
    const pollInterval = 5000;

    setSetupPromptLoading(true);
    setSetupPromptError(result?.mcp?.setup_prompt_error || null);

    async function pollSetupPrompt() {
      while (!cancelled && attempts < maxAttempts) {
        attempts++;
        try {
          const handoff = await getAppCursorRules(result.app_id);
          if (cancelled) return;
          const raw = typeof handoff?.setup_prompt === 'string' ? handoff.setup_prompt : null;
          const prompt = raw;
          if (prompt) {
            setSetupPrompt(prompt);
            setSetupPromptError(null);
            setSetupPromptLoading(false);
            return;
          }
        } catch {
          // Retry on transient errors
        }
        if (!cancelled && attempts < maxAttempts) {
          await new Promise(r => setTimeout(r, pollInterval));
        }
      }
      if (cancelled) return;
      // Server-only fallback: the create 201 `mcp.setup_prompt` (orchestrator-api).
      // Never synthesize a client-side setup prompt / rules template.
      const fallback = result?.mcp?.setup_prompt || null;
      setSetupPrompt(fallback);
      setSetupPromptError(fallback ? null : 'Setup prompt not fully ready yet — MCP connection will still work.');
      setSetupPromptLoading(false);
    }

    void pollSetupPrompt();
    return () => {
      cancelled = true;
    };
  }, [provisioning, result]);

  async function handleCreate() {
    if (!name.trim()) { setError('App name is required'); return; }
    if (storageMode === 'dedicated' && mongoTestState.status !== 'ok') {
      setError('Test Mongo connection successfully before creating this app.');
      return;
    }
    setLoading(true);
    setError(null);
    setSetupPrompt(null);
    setSetupPromptError(null);
    try {
      // Start polling for provision status FIRST before creating,
      // so UI instantly snaps to provisioning state.
      setProvisioning(true);
      setProvisioningStatus('initializing');
      setProvisioningLogs(['🚀 Initializing provisioning pipeline...']);
      
      const dedicatedUri = buildDedicatedMongoUri();
      const resp = await createAppInstance({
        name: name.trim(),
        description: description.trim(),
        features,
        async: '1',
        data: storageMode === 'dedicated'
          ? {
            storage_mode: 'dedicated',
            mongo_uri: dedicatedUri,
            source_types: Object.entries(dataSources).filter(([, enabled]) => enabled).map(([id]) => id),
          }
          : {
            storage_mode: 'shared',
            source_types: Object.entries(dataSources).filter(([, enabled]) => enabled).map(([id]) => id),
          },
      });
      setResult(resp);
      
    } catch (err: any) {
      setProvisioning(false);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // If we are currently polling the provisioning status
  if (result && provisioning) {
    return (
      <Card className="border-2 border-blue-200 bg-blue-50/30 dark:bg-blue-950/10">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
            <h3 className="font-semibold text-blue-800 dark:text-blue-200">Provisioning App: {result.name}</h3>
            <Badge variant="outline" className="bg-white">{provisioningStatus || 'provisioning'}</Badge>
          </div>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Agents are currently configuring your database, API keys, and resources in the background. This usually takes 15-30 seconds.
          </p>
          <div className="bg-white dark:bg-gray-900 rounded border p-4 text-xs font-mono space-y-1 text-gray-600 dark:text-gray-400 max-h-48 overflow-y-auto">
            {provisioningLogs.map((log, i) => (
              <div key={i}>{log}</div>
            ))}
            <div className="animate-pulse">_</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If we are fully complete
  if (result && !provisioning) {
    const cursorConfig = JSON.stringify(result.mcp?.cursor_config || {}, null, 2);
    return (
      <Card className="border-2 border-green-200 bg-green-50/30 dark:bg-green-950/10">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Check className="h-5 w-5 text-green-600" />
            <h3 className="font-semibold text-green-800 dark:text-green-200">App Created: {result.name}</h3>
            <Badge variant="secondary">{result.app_id}</Badge>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-lg border p-4 space-y-2">
            <h4 className="text-sm font-semibold">Initialize Cursor Fast</h4>
            <p className="text-xs text-muted-foreground">
              Copy one prompt to create <code>.cursor/mcp.json</code> and <code>.cursor/rules/devcockpit-integration.mdc</code> for this environment.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs"
              disabled={!setupPrompt || setupPromptLoading}
              onClick={() => setupPrompt && copyToClipboard(setupPrompt, 'setup-prompt')}
            >
              <Copy className="h-3 w-3 mr-1" />
              {copied === 'setup-prompt' ? 'Copied!' : setupPromptLoading ? 'Preparing setup prompt...' : 'Copy Cursor Setup Prompt'}
            </Button>
            {setupPromptError && (
              <p className="text-xs text-amber-700 dark:text-amber-300">{setupPromptError}</p>
            )}
            {!setupPromptError && (
              <p className="text-xs text-muted-foreground">
                The key is not in this prompt — copy it from the MCP key box below into the Cursor connection header. The prompt tells Cursor to create a literal <code>.cursor</code> folder, not a plain <code>cursor</code> folder, so you do not need to open the app workspace first.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* MCP Connection */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border p-4 space-y-2">
              <h4 className="text-sm font-semibold">Connect Cursor IDE</h4>
              <p className="text-xs text-muted-foreground">Paste this into your project's <code>.cursor/mcp.json</code></p>
              <pre className="bg-muted p-2 rounded text-[10px] leading-4 overflow-x-auto max-h-32">{cursorConfig}</pre>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => copyToClipboard(cursorConfig, 'config')}>
                <Copy className="h-3 w-3 mr-1" />
                {copied === 'config' ? 'Copied!' : 'Copy Cursor Config'}
              </Button>
            </div>

            {/* App URLs */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border p-4 space-y-2">
              <h4 className="text-sm font-semibold">App URLs</h4>
              <div className="space-y-1.5 text-xs">
                <div>
                  <span className="text-muted-foreground">MCP Endpoint:</span>
                  <code className="ml-1 bg-muted px-1 rounded">{result.mcp?.endpoint}</code>
                </div>
                <div>
                  <span className="text-muted-foreground">App URL:</span>
                  <code className="ml-1 bg-muted px-1 rounded">{result.hosting?.url}</code>
                </div>
                <div>
                  <span className="text-muted-foreground">Subdomain:</span>
                  <code className="ml-1 bg-muted px-1 rounded">{result.hosting?.subdomain_url}</code>
                </div>
              </div>
              <Button variant="default" size="sm" className="w-full text-xs mt-2" onClick={() => onCreated(result)}>
                Open Builder
              </Button>
            </div>
          </div>

          {result.mcp?.key && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded p-3 text-xs space-y-1">
              <p className="font-medium text-amber-800 dark:text-amber-200">MCP key (shown once) — replace <code>&lt;your_mcp_key&gt;</code> in the Cursor connection header with it:</p>
              <code className="text-amber-700 dark:text-amber-300 break-all block">{result.mcp.key}</code>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => copyToClipboard(result.mcp.key, 'mcp-key')}>
                <Copy className="h-3 w-3 mr-1" />
                {copied === 'mcp-key' ? 'Copied!' : 'Copy MCP Key'}
              </Button>
            </div>
          )}

          {/* API Key Warning */}
          {result.api_key?.key && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded p-3 text-xs">
              <p className="font-medium text-amber-800 dark:text-amber-200">Save your API key (shown once):</p>
              <code className="text-amber-700 dark:text-amber-300 break-all">{result.api_key.key}</code>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6 space-y-5">
        <div>
          <h3 className="text-lg font-semibold">Create New App Instance</h3>
          <p className="text-sm text-muted-foreground">Set up a new application with sensible defaults. Entity Manager and Firebase Auth are always included.</p>
        </div>

        {/* Name & Description */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">App Name</label>
            <Input placeholder="e.g., FarmOps Hub" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Description (optional)</label>
            <Input
              placeholder="Short summary of what you're building"
              value={description}
              maxLength={APP_DESCRIPTION_MAX}
              onChange={(e) => setDescription(e.target.value.slice(0, APP_DESCRIPTION_MAX))}
            />
            <div className={`mt-1 text-[10px] text-right ${description.length >= APP_DESCRIPTION_MAX ? 'text-amber-600' : 'text-muted-foreground'}`}>
              {description.length}/{APP_DESCRIPTION_MAX}
            </div>
          </div>
        </div>

        {/* Core Features */}
        <div>
          <h4 className="text-sm font-medium mb-2">Core Features (always included)</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {CORE_FEATURES.map(f => (
              <div key={f.id} className="flex items-center gap-2 bg-muted/50 rounded px-3 py-2 text-xs">
                <input type="checkbox" checked disabled className="h-3.5 w-3.5 rounded opacity-50" />
                <f.icon className="h-3.5 w-3.5 text-muted-foreground" />
                <div>
                  <span className="font-medium">{f.label}</span>
                  <span className="text-muted-foreground ml-1 hidden md:inline">-- {f.description}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Optional Features */}
        <div>
          <h4 className="text-sm font-medium mb-2">Optional Features</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {OPTIONAL_FEATURES.map(f => (
              <button
                key={f.id}
                type="button"
                className={`flex items-center gap-2 rounded px-3 py-2.5 text-xs border text-left transition-colors ${
                  features[f.id]
                    ? 'bg-primary/10 border-primary/30 text-foreground'
                    : 'bg-background border-border hover:bg-muted/50 text-muted-foreground'
                }`}
                onClick={() => toggleFeature(f.id)}
              >
                <input type="checkbox" checked={!!features[f.id]} readOnly className="h-3.5 w-3.5 rounded pointer-events-none" />
                <f.icon className="h-4 w-4" />
                <div>
                  <span className="font-medium">{f.label}</span>
                  <span className="block text-[10px] opacity-75">{f.description}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Data Strategy */}
        <div>
          <h4 className="text-sm font-medium mb-2">Data Strategy</h4>
          <div role="radiogroup" aria-label="Data Strategy" className="space-y-2">
            {STORAGE_MODE_OPTIONS.map((option) => {
              const checked = storageMode === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  tabIndex={checked ? 0 : -1}
                  ref={(node) => {
                    storageModeRefs.current[option.id] = node;
                  }}
                  onClick={() => setStorageMode(option.id)}
                  onKeyDown={(event) => handleStorageModeKeyDown(event, option.id)}
                  className={`w-full rounded border px-3 py-2 text-left text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                    checked
                      ? 'border-primary/40 bg-primary/10 text-foreground'
                      : 'border-border bg-background hover:bg-muted/50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border ${checked ? 'border-primary' : 'border-muted-foreground/50'}`}>
                      <span className={`h-2 w-2 rounded-full ${checked ? 'bg-primary' : 'bg-transparent'}`} />
                    </span>
                    <span>{option.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {storageMode === 'dedicated' && (
            <div className="mt-3 border rounded p-3 bg-muted/20 space-y-3">
              <div className="text-xs font-medium text-muted-foreground">
                Provide full URI or split details, then test before creating.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input
                  value={extMongoHost}
                  onChange={(e) => setExtMongoHost(e.target.value)}
                  placeholder="cluster0.abc.mongodb.net"
                />
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={extMongoUseSrv}
                    onChange={(e) => setExtMongoUseSrv(e.target.checked)}
                    className="rounded"
                  />
                  Use SRV (mongodb+srv://)
                </div>
                <Input
                  value={extMongoUser}
                  onChange={(e) => setExtMongoUser(e.target.value)}
                  placeholder="username"
                />
                <Input
                  type="password"
                  value={extMongoPass}
                  onChange={(e) => setExtMongoPass(e.target.value)}
                  placeholder="password"
                />
                <Input
                  value={extMongoDb}
                  onChange={(e) => setExtMongoDb(e.target.value)}
                  placeholder="database name (optional)"
                  className="md:col-span-2"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground block">Or full Mongo URI</label>
                <Input
                  type="password"
                  value={dedicatedMongoUri}
                  onChange={(e) => setDedicatedMongoUri(e.target.value)}
                  placeholder="mongodb+srv://user:pass@cluster.mongodb.net/EnbiosisIntelligene"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleTestConnection}
                  disabled={mongoTestState.status === 'testing'}
                >
                  {mongoTestState.status === 'testing' ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Testing...
                    </>
                  ) : 'Test connection'}
                </Button>
                {mongoTestState.status === 'ok' && (
                  <span className="text-xs text-green-700">Connection successful</span>
                )}
                {mongoTestState.status === 'error' && (
                  <span className="text-xs text-red-600">{mongoTestState.message}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Runtime Data Sources */}
        <div>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <h4 className="text-sm font-medium">Data this app can read</h4>
              <p className="text-xs text-muted-foreground mt-1">
                These create broad API Expose bindings during app setup. You can fine-tune scopes, filters, and rate limits later in API Expose.
              </p>
            </div>
            <Badge variant="outline" className="text-[10px]">Seed API Expose</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {DATA_SOURCE_OPTIONS.map(source => (
              <button
                key={source.id}
                type="button"
                className={`flex items-center gap-2 rounded px-3 py-2.5 text-xs border text-left transition-colors ${
                  dataSources[source.id]
                    ? 'bg-primary/10 border-primary/30 text-foreground'
                    : 'bg-background border-border hover:bg-muted/50 text-muted-foreground'
                }`}
                onClick={() => toggleDataSource(source.id)}
              >
                <input type="checkbox" checked={!!dataSources[source.id]} readOnly className="h-3.5 w-3.5 rounded pointer-events-none" />
                <source.icon className="h-4 w-4 shrink-0" />
                <div>
                  <span className="font-medium">{source.label}</span>
                  <span className="block text-[10px] opacity-75">{source.description}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}

        <Button
          onClick={handleCreate}
          disabled={loading || !name.trim() || (storageMode === 'dedicated' && mongoTestState.status !== 'ok')}
          className="w-full md:w-auto"
        >
          {loading ? 'Creating...' : 'Create App Instance'}
        </Button>
      </CardContent>
    </Card>
  );
}
